import React, { useState, useRef, useEffect } from 'react';
import { PencilLine, Check, X, Loader2 } from 'lucide-react';

export default function InlineEditCell({ value, onSave, rowId, type = 'text', placeholder = '', className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value ?? ''); }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const validate = (val) => {
    if (type === 'number') {
      if (val === '') return '';
      if (isNaN(Number(val)) || Number(val) < 0) return 'Must be a valid number';
    }
    if (type === 'email') {
      if (val === '') return '';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return 'Invalid email format';
    }
    return '';
  };

  const handleSave = async () => {
    const formatted = type === 'number' && draft !== '' ? parseFloat(draft) : draft;
    if (formatted === value || (formatted === '' && (value === null || value === undefined || value === ''))) {
      setEditing(false);
      setError('');
      return;
    }
    const err = validate(draft);
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(rowId, formatted);
      setEditing(false);
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(value ?? '');
    setEditing(false);
    setError('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
    else if (e.key === 'Escape') { handleCancel(); }
  };

  const displayValue = type === 'number'
    ? (value != null ? parseFloat(value).toFixed(2) : '0.00')
    : (value || placeholder);

  if (editing) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <input
          ref={inputRef}
          type={type === 'number' ? 'text' : type}
          inputMode={type === 'number' ? 'decimal' : undefined}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(''); }}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className={`flex-1 text-sm px-2 py-1 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-ring font-[inherit] ${
            type === 'number' ? 'text-right w-[80px]' : 'w-full min-w-[140px]'
          } ${error ? 'border-destructive ring-destructive/30' : ''}`}
          disabled={saving}
          data-testid="inline-edit-input"
        />
        {error && <span className="text-[10px] text-destructive whitespace-nowrap">{error}</span>}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleSave}
          disabled={saving}
          className="p-0.5 rounded hover:bg-primary/10 text-primary transition-colors duration-150"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </button>
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleCancel}
          disabled={saving}
          className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground transition-colors duration-150"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`group flex items-center gap-1.5 cursor-pointer py-0.5 px-1.5 -mx-1.5 rounded hover:bg-muted/50 transition-colors duration-150 ${className}`}
      onClick={() => setEditing(true)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && setEditing(true)}
    >
      <span className={`text-sm flex-1 ${type === 'number' ? 'font-medium text-right' : 'truncate'} ${!value ? 'text-muted-foreground italic' : ''}`}
        data-role={type === 'number' ? 'mono' : undefined}
      >
        {displayValue}
      </span>
      <PencilLine className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0" />
    </div>
  );
}
